import React, { useMemo } from 'react'
import { AsyncSelectInput, SelectInput } from '@demandlocal/components'
import { Option } from '../../../../lib/types/Option'
import TextInput from '../../../../components/FormElements/TextInput'
import { useGetAdminClients } from '../../../../api/admin/useGetAdminClients'
import { EleadSubscription } from '../../../../lib/types/Elead/EleadSubscription'
import { useGetVinSolutionsDealerships } from '../../../../api/admin/useGetVinSolutionsDealerships'
import { useGetEleadSubscriptions } from '../../../../api/admin/useGetEleadSubscriptions'
import { useGetCDKFiSalesSubscriptions } from '../../../../api/admin/useGetCDKFiSalesSubscriptions'
import { useGetAmazonAdsAccounts } from '../../../../api/admin/useGetAmazonAdsDealerships'
import { useGetProcedeDealerships } from '../../../../api/admin/useGetProcedeDealerships'
import { useGetPromaxDealerships } from '../../../../api/admin/useGetPromaxDealerships'

export default function DealershipIntegrations(): JSX.Element {
  const { data: vinSolutionsDealerships, isLoading: isLoadingVinSolutionsDealerships } =
    useGetVinSolutionsDealerships()

  const { data: eleadSubscriptions, isLoading: isLoadingEleadSubscriptions } =
    useGetEleadSubscriptions()

  const { data: amazonAccounts, isLoading: isLoadingAmazonAdsAccounts } =
    useGetAmazonAdsAccounts()

  const { data: cdkFiSalesSubscriptions, isLoading: isLoadingCDKFiSalesSubscriptions } =
    useGetCDKFiSalesSubscriptions()

  const { data: procedeDealerships, isLoading: isLoadingProcedeDealerships } =
    useGetProcedeDealerships()

  const { data: promaxDealerships, isLoading: isLoadingPromaxDealerships } =
    useGetPromaxDealerships()

  const vinSolutionsDealershipsOptions = useMemo(() => {
    if (isLoadingVinSolutionsDealerships) return []
    return vinSolutionsDealerships?.map((dealership) => ({
      label: `${dealership?.Name} - ${dealership?.City}, ${dealership?.State}`,
      value: dealership?.DealerId,
    }))
  }, [isLoadingVinSolutionsDealerships, vinSolutionsDealerships])

  const eleadSubscriptionsOptions = useMemo(() => {
    if (isLoadingEleadSubscriptions) return []
    return eleadSubscriptions?.map((subscription: EleadSubscription) => ({
      label: `${subscription?.orgName} (${subscription?.subscriptionId})`,
      value: subscription?.subscriptionId,
      disabled: subscription?.deactivationDate?.length > 0,
    }))
  }, [isLoadingEleadSubscriptions, eleadSubscriptions])

  const cdkFiSalesSubscriptionsOptions = useMemo(() => {
    if (isLoadingCDKFiSalesSubscriptions) return []
    return cdkFiSalesSubscriptions?.map((subscription: EleadSubscription) => ({
      label: `${subscription?.orgName} (${subscription?.subscriptionId})`,
      value: subscription?.subscriptionId,
      disabled: subscription?.deactivationDate?.length > 0,
    }))
  }, [isLoadingCDKFiSalesSubscriptions, cdkFiSalesSubscriptions])
  return (
    <div className="flex flex-col gap-8 [&>*]:pt-8 last:pb-8 divide-y-2">
      <div className="flex flex-col gap-5">
        <span className="text-xs font-bold">
          Assigning an account does not create a customer source, that can be done in
          Audiences {'>'} Customers
        </span>
        <AsyncSelectInput
          label={'Demand Local Customer'}
          placeholder={'Choose a client'}
          name={'client_id'}
          fetchFn={useGetAdminClients}
          customLabelFormat={'{0} ({1})'}
          searchKeyName={['client_display_name', 'agency_name']}
          labelKeyName={['client_display_name', 'agency_name']}
          valueKeyName={'client_id'}
          isClearable={true}
        />
      </div>
      <SelectInput
        isLoading={isLoadingVinSolutionsDealerships}
        options={vinSolutionsDealershipsOptions as Option[]}
        placeholder={'Vin Solutions Dealership'}
        name={'vin_solutions_dealership'}
        label={'Vin Solutions Dealership'}
        isClearable={true}
      />
      <SelectInput
        isLoading={isLoadingAmazonAdsAccounts}
        isOptionDisabled={(option) => option.disabled}
        options={amazonAccounts}
        placeholder={'Amazon Ads Account'}
        name={'amazon_ads_account_id'}
        label={'Amazon Ads Account'}
        isClearable={true}
      />
      <div className="flex flex-col gap-2">
        <SelectInput
          isLoading={isLoadingEleadSubscriptions}
          isOptionDisabled={(option) => option.disabled}
          options={eleadSubscriptionsOptions as Option[]}
          placeholder={'Elead Subscription'}
          name={'elead_subscription'}
          label={'Elead Subscription'}
          isClearable={true}
        />
        <a
          className="text-xs font-bold text-primary-persian-blue"
          href="https://marketplace.fortellis.io/solutions/linkone/linkone-eleads?id=6007821d-3a1e-4fbf-b613-ed53aa28739a"
          target="_blank"
          rel="noreferrer">
          Learn more about this subscription here
        </a>
      </div>
      <div className="flex flex-col gap-2">
        <SelectInput
          isLoading={isLoadingCDKFiSalesSubscriptions}
          isOptionDisabled={(option) => option.disabled}
          options={cdkFiSalesSubscriptionsOptions as Option[]}
          placeholder={'CDK FI Sales Subscription'}
          name={'cdk_fi_sales_subscription'}
          label={'CDK FI Sales Subscription'}
          isClearable={true}
        />
        <a
          className="text-xs font-bold text-primary-persian-blue"
          href="https://marketplace.fortellis.io/solutions/linkone/linkone-cdk?id=86780009-4382-461d-a388-5f25daa41da1"
          target="_blank"
          rel="noreferrer">
          Learn more about this subscription here
        </a>
      </div>
      <SelectInput
        isLoading={isLoadingProcedeDealerships}
        options={procedeDealerships as Option[]}
        placeholder={'Procede Dealership ID'}
        name={'procede_dealership_id'}
        label={'Procede Dealership ID'}
        isClearable={true}
      />
      <SelectInput
        isLoading={isLoadingPromaxDealerships}
        options={promaxDealerships as Option[]}
        placeholder={'Promax Dealership ID'}
        name={'promax_dealership_id'}
        label={'Promax Dealership ID'}
        isClearable={true}
      />
      <TextInput
        inverted={true}
        placeholder={'Dealersocket Dealership ID'}
        name={'dealersocket_dealership_id'}
        label={'Dealersocket Dealership ID'}
      />
      <TextInput
        inverted={true}
        placeholder={'Tekion Dealership ID'}
        name={'tekion_dealership_id'}
        label={'Tekion Dealership ID'}
      />
      <TextInput
        inverted={true}
        placeholder={'Drive Centric Dealership ID'}
        name={'drivecentric_dealership_id'}
        label={'Drive Centric Dealership ID'}
      />
      <div className={'grid grid-cols-2 gap-4'}>
        <TextInput
          inverted={true}
          placeholder={'TradeDesk Advertiser ID'}
          name={'ttd_advertiser_id'}
          label={'TradeDesk Advertiser ID'}
        />
        <TextInput
          inverted={true}
          placeholder={'TradeDesk Secret Key'}
          name={'ttd_secret_key'}
          label={'TradeDesk Secret Key'}
        />
      </div>
    </div>
  )
}
